import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { useDispatch } from 'react-redux';
import {
  addToCart,
  addToCartQuantity,
  getTotals,
  removeCartItem,
  selectUpdatingCartItems,
} from '../../../redux/cartSlice';
import { exitEditingCurrentOrder } from '../../../redux/cartActions';
import { selectOrderEditSnapshot } from '../../../redux/OrdersSlice';
import { useAppSelector } from '../../../redux/helper';
import { selectCartItems, selectCartOrderData } from '../../../redux/cartSlice';
import ErrorContext from '../../../Context/ErrorContext';
import { ExtrasContext } from '../../../Context/ExtrasContext';
import { AppSnackbar } from '../../../services/AppSnackbar';
import { AppAlert } from '../../../services/AppAlert';
import {
  formatNumberDecimal,
  forceHttps,
  fs,
  s,
  v,
  iconSize,
  borderWidth,
  replaceCurrencyString,
  isNumericEffectivelyZero,
  formatMoney,
  roundToNumericPrecision,
} from '../../../utils/libs';
import {
  selectQuantityButtons,
  selectShowProductImages,
  selectUseGestureQuantity,
} from '../../../redux/SettingsSlice';
import { GTFonts, useLayoutSize, useTheme } from '../../../theme';
import { WEB_NAV_SIDEBAR_RAIL_S } from '../../../theme/webLayoutWidths';
import { getProductGridNumColumns } from '../../../utils/layoutUtils';
import type { CartItem } from '../../../redux/cartSlice';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import QuantityControls from '../../../components/Reusables/QuantityControls';
import { getProductDisplayName } from '../../../utils/productDisplayName';
import {
  getVariantCartTotal,
  getVariantPartialAttention,
} from '../../../utils/variantHelpers';
import {
  appendServiceMeta,
  isStockTracked,
  type ProductKind,
} from '../../../utils/productKind';

export interface ProductComponentObjectWeb {
  category: string;
  itemname: string;
  price: number;
  quantity: number;
  productID: string;
  variantId?: string;
  mass?: number;
  unitofmass?: string;
  model?: string;
  size?: string;
  msl?: number;
  price_type?: 'item' | 'unit' | string;
  /** A service keeps no stock: no stock gate, badge or dimmed plus. */
  kind?: ProductKind;
  index: number;
  image?: string;
  isFavourite?: boolean;
  hasVariants?: boolean;
  variantCount?: number;
  variants?: any[];
  variantAttributes?: string[];
  priceRangeText?: string;
  handleSubproductQuantity: (product: CartItem) => void;
  showbottomSheetFn?: (product: CartItem) => void;
  onProductPress?: (product: any) => void;
  onMissingImagePress?: (product: any) => void;
  /**
   * Fires when a TextInput inside this card gains focus. Parent list uses
   * this to scroll the row above the on-screen keyboard.
   */
  onCardInputFocus?: () => void;
}

const ProductItem: React.FC<ProductComponentObjectWeb> = React.memo(
  ({
    handleSubproductQuantity,
    category,
    itemname,
    price,
    quantity,
    productID,
    variantId,
    mass,
    unitofmass,
    model,
    size,
    msl,
    index,
    price_type,
    kind,
    image,
    isFavourite,
    hasVariants = false,
    variantCount = 0,
    variants = [],
    variantAttributes = [],
    priceRangeText,
    showbottomSheetFn,
    onProductPress,
    onMissingImagePress,
    onCardInputFocus,
  }) => {
    const { theme } = useTheme();
    const styles = useMemo(() => createStyles(theme), [theme]);
    const dispatch = useDispatch();
    const [massValues, setMassValues] = useState<{ [key: string]: string }>({});

    const {
      handleError,
      textInputError,
      setTextInputError,
      quantityInputError,
      setQuantityInputError,
    } = useContext(ErrorContext);

    const orderDetails = useAppSelector(state => state?.orderdetails?.order);
    const cartItems = useAppSelector(selectCartItems);
    const cartOrderData = useAppSelector(selectCartOrderData);
    // Paid/unpaid figures belong to the ORDER, so they are read from the
    // snapshot taken before editing started, not from the live cart. The cart
    // entry is dropped the moment the line is decremented to zero, which used
    // to make the status label vanish while the order line still existed.
    const originalOrderItem = useAppSelector(
      state =>
        selectOrderEditSnapshot(state).find(
          doc =>
            doc.productID === productID &&
            (doc.variantId ?? null) === (variantId ?? null),
        ) ?? null,
    );
    const updatingCartitems = useAppSelector(selectUpdatingCartItems);
    const showProductImages = useAppSelector(selectShowProductImages);
    const useGestureQuantity = useAppSelector(selectUseGestureQuantity);
    const quantityButtons = useAppSelector(selectQuantityButtons);
    const { isRowLayout } = useContext(ExtrasContext);
    const { useSidebarNav: isSidebarLayout } = useLayoutSize();
    const { width } = useWindowDimensions();
    // Actual rendered cell width (accounts for sidebar + alpha rail). Fallback until onLayout.
    const [cellWidth, setCellWidth] = useState(0);

    const gridMetrics = useMemo(() => {
      const contentWidth = isSidebarLayout
        ? Math.max(width - s(WEB_NAV_SIDEBAR_RAIL_S), width * 0.5)
        : width;
      const cols = getProductGridNumColumns(contentWidth);
      // productItemWrapper paddingHorizontal s(4) each side
      const estimatedColWidth = contentWidth / cols - s(8);
      return { cols, estimatedColWidth };
    }, [width, isSidebarLayout]);

    // Height from *cell* width, not viewport/2.
    // ≤2 cols: phone aspect (1.4×). Multi-col: ~1.05× so meta fits with almost no dead space.
    const itemSize = useMemo(() => {
      const w =
        cellWidth > 0 ? cellWidth : gridMetrics.estimatedColWidth;
      return gridMetrics.cols <= 2 ? w * 1.4 : w * 1.05;
    }, [cellWidth, gridMetrics]);

    const verticalImageHeight = useMemo(() => itemSize * 0.5, [itemSize]);

    const useCompactControls = gridMetrics.cols > 2;

    const onColumnCardLayout = useCallback(
      (e: { nativeEvent: { layout: { width: number } } }) => {
        const w = e.nativeEvent.layout.width;
        if (w > 0 && Math.abs(w - cellWidth) > 0.5) {
          setCellWidth(w);
        }
      },
      [cellWidth],
    );

    const cartItem = useMemo(
      () =>
        cartItems?.find(
          i =>
            i.productID === productID &&
            (i.variantId ?? null) === (variantId ?? null),
        ),
      [cartItems, productID, variantId],
    );

    // Parent variant card has no cart line of its own; total its variants'.
    const variantCartTotal = useMemo(
      () =>
        hasVariants ? getVariantCartTotal(cartItems, productID, price_type) : 0,
      [hasVariants, cartItems, productID, price_type],
    );

    const currency = useAppSelector(
      state =>
        state.location.currentLocation?.bp_location_settings
          ?.bp_location_currency || 'KES',
    );

    const memoizedProduct = useMemo(
      () => ({
        category,
        itemname,
        quantity,
        price,
        mass,
        unitofmass,
        productID,
        variantId,
        model,
        size,
        msl,
        price_type,
        kind,
        image,
        isFavourite,
        hasVariants,
        variantCount,
        variants,
        variantAttributes,
        priceRangeText,
      }),
      [
        category,
        itemname,
        quantity,
        price,
        mass,
        unitofmass,
        productID,
        variantId,
        model,
        size,
        msl,
        price_type,
        kind,
        image,
        isFavourite,
        hasVariants,
        variantCount,
        variants,
        variantAttributes,
        priceRangeText,
      ],
    );

    // Gesture mode excludes unit products - matches native
    const isGestureMode = useMemo(
      () => !!useGestureQuantity && price_type !== 'unit',
      [useGestureQuantity, price_type],
    );
    const useGestureForCard = useMemo(
      () => isGestureMode && !hasVariants,
      [isGestureMode, hasVariants],
    );
    // A service keeps no stock: every stock comparison below stands down
    const stockTracked = isStockTracked(kind);
    const inStock = !stockTracked || quantity > 0;

    // Validation function for quantity errors - matches native Product.tsx
    const validateQuantityError = useCallback(
      (newQuantity: number, productId: string) => {
        const ci = cartItems?.find(
          item =>
            item.productID === productId &&
            (item.variantId ?? null) === (variantId ?? null),
        );
        if (!ci) return;

        if (updatingCartitems) {
          if (newQuantity < (Number(ci.bp_order_paid_quantity) ?? 0)) {
            setQuantityInputError?.((prev: any) => ({
              ...(prev || {}),
              [productId]: true,
            }));
          } else {
            setQuantityInputError?.((prev: any) => ({
              ...(prev || {}),
              [productId]: false,
            }));
          }
        }

        if (!stockTracked || newQuantity <= quantity) {
          setQuantityInputError?.((prev: any) => ({
            ...(prev || {}),
            [productId]: false,
          }));
        }
      },
      [
        updatingCartitems,
        cartItems,
        quantity,
        setQuantityInputError,
        variantId,
      ],
    );

    const onAdd = useCallback(() => {
      if (!inStock) {
        AppSnackbar.show(`${itemname} is out of stock`, 'warning', {
          durationMs: 4500,
        });
        return;
      }

      // Check if adding one more would exceed available stock
      if (stockTracked && price_type === 'item' && cartItem) {
        const currentCartQty = cartItem.Quantity ?? 0;
        const originalQty = cartItem.bp_sale_original_quantity ?? 0;
        const availableStock = (quantity ?? 0) + originalQty;

        if (currentCartQty >= availableStock) {
          AppSnackbar.show(
            `${getProductDisplayName({ itemname, mass, unitofmass, size, model, price_type })} is out of stock`,
            'warning',
            { durationMs: 4500 },
          );
          return;
        }
      }

      const payload = originalOrderItem
        ? {
            ...(memoizedProduct as any),
            bp_order_paid_quantity: originalOrderItem.bp_order_paid_quantity,
            bp_order_paid_mass: originalOrderItem.bp_order_paid_mass,
            bp_order_completed: originalOrderItem.bp_order_completed,
            bp_sale_original_quantity: originalOrderItem.bp_sale_original_quantity,
          }
        : memoizedProduct;
      dispatch(addToCart(payload as any));
      dispatch(getTotals());

      // Validate error state after adding - matches native behavior
      if (price_type === 'item') {
        const newQuantity = (cartItem?.Quantity ?? 0) + 1;
        validateQuantityError(newQuantity, productID);
      }
    }, [
      dispatch,
      memoizedProduct,
      originalOrderItem,
      inStock,
      itemname,
      price_type,
      cartItem,
      quantity,
      mass,
      unitofmass,
      size,
      model,
      validateQuantityError,
      productID,
    ]);

    const onSub = useCallback(() => {
      if (!cartItems || !cartOrderData) return;

      const orderType = cartOrderData.orderType?.toLowerCase();

      if (orderType === 'partial') {
        const isPaidItem = cartItems.some(ci => {
          if (ci.productID !== productID) return false;
          const paid = Number(ci.bp_order_paid_quantity ?? 0);
          const qty = Number(ci.Quantity ?? 0);
          return paid > 0 && qty <= paid;
        });

        if (isPaidItem) {
          AppSnackbar.show(
            'This item is fully or partially paid. It cannot be removed. Cancel the order instead.',
            'warning',
            { durationMs: 5000 },
          );
          return;
        }
      }

      const cartProduct = cartItems[0];
      const willEmptyPending =
        orderType === 'pending' &&
        cartItems.length === 1 &&
        cartProduct.productID === productID &&
        (cartProduct.variantId ?? null) === (variantId ?? null) &&
        cartProduct.Quantity === 1;

      if (willEmptyPending) {
        AppAlert.alert(
          'Remove Item ?',
          'This is the last item in a pending order. Removing it will cancel the order.',
          [
            { text: 'Cancel', style: 'cancel', onPress: () => {} },
            {
              text: 'Remove',
              style: 'destructive',
              onPress: () => {
                handleSubproductQuantity(memoizedProduct as any);
                dispatch(exitEditingCurrentOrder() as any);
                if (price_type === 'item') {
                  const newQuantity = (cartItem?.Quantity ?? 0) - 1;
                  validateQuantityError(newQuantity, productID);
                }
              },
            },
          ],
        );
        return;
      }

      handleSubproductQuantity(memoizedProduct as any);

      // Validate error state after subtracting - matches native behavior
      if (price_type === 'item') {
        const newQuantity = (cartItem?.Quantity ?? 0) - 1;
        validateQuantityError(newQuantity, productID);
      }
    }, [
      dispatch,
      cartItems,
      cartOrderData,
      productID,
      variantId,
      handleSubproductQuantity,
      memoizedProduct,
      price_type,
      cartItem,
      validateQuantityError,
    ]);

    const handleQuantityInputChange = useCallback(
      (input: string) => {
        let sanitizedInput: number;
        const digitInput = input.replace(/^0+(?=\d)/, '');
        sanitizedInput = parseInt(digitInput) || 0;
        const cartItem = cartItems?.find(
          item =>
            item.productID === productID &&
            (item.variantId ?? null) === (variantId ?? null),
        );

        if (updatingCartitems) {
          if (
            cartItem &&
            cartItem.bp_order_paid_quantity &&
            Number(digitInput) < (Number(cartItem.bp_order_paid_quantity) ?? 0)
          ) {
            dispatch(
              addToCartQuantity({
                productID,
                variantId,
                quantity: Number(sanitizedInput),
              }),
            );
            setQuantityInputError?.((prevErrors: any) => ({
              ...prevErrors,
              [productID]: true,
            }));
            return;
          } else {
            setQuantityInputError?.((prevErrors: any) => ({
              ...prevErrors,
              [productID]: false,
            }));
          }
        }

        // product mass is always null so we use this orderdata to calc original product details
        const originalProduct = cartOrderData?.order?.find(
          (product: any) => product.bp_product_information._id === productID,
        );

        if (
          updatingCartitems &&
          originalProduct?.bp_product_information?.bp_product_price_type
        ) {
          const productInfo = originalProduct?.bp_product_information;

          const totalProductQuantity =
            productInfo.bp_product_price_type === 'unit'
              ? productInfo.bp_product_quantity +
                (originalProduct?.bp_order_mass ?? 0)
              : productInfo.bp_product_quantity +
                (originalProduct?.bp_order_quantity ?? 0);

          if (Number(sanitizedInput) <= 0 || sanitizedInput === null) {
            setQuantityInputError?.((prevErrors: any) => ({
              ...prevErrors,
              [productID]: true,
            }));
          }

          if (
            !stockTracked ||
            Number(sanitizedInput) <= Number(totalProductQuantity)
          ) {
            dispatch(
              addToCartQuantity({
                productID,
                variantId,
                quantity: Number(sanitizedInput),
              }),
            );
            setQuantityInputError?.((prevErrors: any) => ({
              ...prevErrors,
              [productID]: false,
            }));
          } else {
            if (Number(sanitizedInput) >= 0) {
              const unpaidQuantity =
                (originalProduct?.bp_order_quantity ?? 0) -
                (originalProduct?.bp_order_paid_quantity ?? 0);
              const totalProductQuantityCalc =
                memoizedProduct.quantity +
                unpaidQuantity +
                (originalProduct?.bp_order_paid_quantity ?? 0);
              if (Number(sanitizedInput) > Number(totalProductQuantityCalc)) {
                setQuantityInputError?.((prevErrors: any) => ({
                  ...prevErrors,
                  [productID]: true,
                }));
                handleError?.(
                  `value is greater than available stock of ${formatNumberDecimal(
                    totalProductQuantityCalc,
                  )}`,
                  'error',
                  true,
                );
                return;
              }
            }
          }
        } else {
          if (stockTracked && Number(sanitizedInput) > Number(quantity)) {
            // setQuantityInputError?.((prevErrors: any) => ({
            //   ...prevErrors,
            //   [productID]: true,
            // }));
            handleError?.(
              `value is greater than available stock of ${formatNumberDecimal(
                quantity,
              )}`,
              'error',
              true,
            );
            return;
          }
          if (sanitizedInput === 0) {
            dispatch(removeCartItem({ productID, variantId }));
            dispatch(getTotals());
          } else {
            dispatch(
              addToCartQuantity({
                productID,
                variantId,
                quantity: Number(sanitizedInput),
                product: memoizedProduct as CartItem,
              }),
            );
            dispatch(getTotals());
          }
          setQuantityInputError?.((prevErrors: any) => ({
            ...prevErrors,
            [productID]: false,
          }));
        }
      },
      [
        cartItems,
        productID,
        updatingCartitems,
        setQuantityInputError,
        cartOrderData,
        dispatch,
        quantity,
        handleError,
        memoizedProduct,
        variantId,
      ],
    );

    // Helper to add product mass to cart
    const onAddProductMass = useCallback(
      (product: CartItem) => {
        const payload = originalOrderItem
          ? {
              ...product,
              bp_order_paid_quantity: originalOrderItem.bp_order_paid_quantity,
              bp_order_paid_mass: originalOrderItem.bp_order_paid_mass,
              bp_order_completed: originalOrderItem.bp_order_completed,
              bp_sale_original_quantity: originalOrderItem.bp_sale_original_quantity,
            }
          : product;
        dispatch(addToCart(payload));
        dispatch(getTotals());
      },
      [dispatch, originalOrderItem],
    );

    // Handle unit (mass) input change - matches native handleInputChange
    const handleMassInputChange = useCallback(
      (input: string) => {
        try {
          let sanitizedInput = input
            .replace(',', '.')
            .replace(/[^0-9.]/g, '')
            .replace(/^0+(?=\d)/, '');
          if (sanitizedInput.startsWith('.')) {
            sanitizedInput = `0${sanitizedInput}`;
          }
          if (!/^\d*\.?\d*$/.test(sanitizedInput)) {
            return;
          }

          const cartItem = cartItems?.find(
            item =>
              item.productID === productID &&
              (item.variantId ?? null) === (variantId ?? null),
          );
          if (updatingCartitems) {
            if (
              cartItem &&
              Number(sanitizedInput) <
                (Number(cartItem.bp_order_paid_mass) ?? 0)
            ) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: true,
              }));
            } else {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: false,
              }));
            }
          }

          if (sanitizedInput === '') {
            setMassValues(prevValues => ({
              ...prevValues,
              [productID]: '',
            }));
            const paidMass = Number(cartItem?.bp_order_paid_mass ?? 0);
            if (paidMass > 0) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: true,
              }));
              return;
            }
            setTextInputError?.((prevErrors: any) => ({
              ...prevErrors,
              [productID]: false,
            }));
            // Unpaid pending lines should remove on clear (same as item qty).
            // Paid lines already returned above; blur restores paid floor.
            // if (!updatingCartitems) {
            return dispatch(removeCartItem({ productID, variantId }));
            // }
          }

          setMassValues(prevValues => ({
            ...prevValues,
            [productID]: sanitizedInput,
          }));

          if (sanitizedInput.endsWith('.')) {
            return;
          }

          const newQuantity = Number.parseFloat(sanitizedInput);
          if (!Number.isFinite(newQuantity) || newQuantity <= 0) {
            return;
          }

          // product mass is always null so we use this orderdata to calc original product details
          const originalProduct = cartOrderData?.order?.find(
            (product: any) => product.bp_product_information._id === productID,
          );
          if (
            updatingCartitems &&
            originalProduct?.bp_product_information?.bp_product_price_type
          ) {
            const productInfo = originalProduct?.bp_product_information;

            const totalProductQuantity =
              productInfo.bp_product_price_type === 'unit'
                ? roundToNumericPrecision(
                    productInfo.bp_product_quantity +
                      (originalProduct?.bp_order_paid_mass ?? 0),
                  )
                : productInfo.bp_product_quantity +
                  (originalProduct?.bp_order_quantity ?? 0);

            if (
              !stockTracked ||
              Number(newQuantity) <= Number(totalProductQuantity)
            ) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: false,
              }));
              onAddProductMass({
                ...memoizedProduct,
                mass: newQuantity,
              } as any);
              return;
            }

            if (Number(newQuantity) > Number(totalProductQuantity)) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: true,
              }));
              handleError?.(
                `value is greater than available stock of ${formatNumberDecimal(
                  totalProductQuantity,
                )} ${memoizedProduct.unitofmass}`,
                'error',
                true,
              );
            }
          } else {
            if (
              !stockTracked ||
              Number(newQuantity) <=
                roundToNumericPrecision(Number(memoizedProduct.quantity))
            ) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: false,
              }));
              onAddProductMass({
                ...memoizedProduct,
                mass: newQuantity,
              } as any);
              return;
            }

            if (
              Number(newQuantity) >
              roundToNumericPrecision(Number(memoizedProduct.quantity))
            ) {
              setTextInputError?.((prevErrors: any) => ({
                ...prevErrors,
                [productID]: true,
              }));
              handleError?.(
                `value is greater than available stock of ${formatNumberDecimal(
                  memoizedProduct.quantity,
                )} ${memoizedProduct.unitofmass}`,
                'error',
                true,
              );
            }
          }
        } catch (error) {
          console.log('handleMassInputChange error', error);
        }
      },
      [
        cartItems,
        productID,
        updatingCartitems,
        memoizedProduct,
        cartOrderData,
        dispatch,
        handleError,
        setTextInputError,
        onAddProductMass,
      ],
    );

    // Get cart item mass for syncing massValues state
    const getCartItemMass = useCallback(() => {
      if (cartItems.length > 0) {
        const cartItem = cartItems?.find(
          item =>
            item.productID === productID &&
            (item.variantId ?? null) === (variantId ?? null),
        );
        return cartItem?.mass == null
          ? ''
          : String(roundToNumericPrecision(cartItem.mass));
      }
      return '';
    }, [cartItems, productID]);

    // Sync massValues with cart items - matches native
    useEffect(() => {
      setMassValues(prevValues => ({
        ...prevValues,
        [productID]: getCartItemMass(),
      }));
    }, [cartItems, productID, getCartItemMass]);

    const handleQuantityBlur = useCallback(() => {
      const live = cartItems?.find(
        item =>
          item.productID === productID &&
          (item.variantId ?? null) === (variantId ?? null),
      );
      const paid = Number(live?.bp_order_paid_quantity ?? 0);
      if (!updatingCartitems || paid <= 0) return;
      if ((live?.Quantity ?? 0) >= paid) return;

      dispatch(
        addToCartQuantity({
          productID,
          variantId,
          quantity: paid,
        }),
      );
      dispatch(getTotals());
      setQuantityInputError?.((prevErrors: any) => ({
        ...prevErrors,
        [productID]: false,
      }));
      AppSnackbar.show(
        'This item is fully or partially paid. It cannot be removed. Cancel the order instead.',
        'warning',
        { durationMs: 5000 },
      );
    }, [
      cartItems,
      productID,
      variantId,
      updatingCartitems,
      dispatch,
      setQuantityInputError,
    ]);

    const handleMassBlur = useCallback(() => {
      const live = cartItems?.find(
        item =>
          item.productID === productID &&
          (item.variantId ?? null) === (variantId ?? null),
      );
      const paid = Number(live?.bp_order_paid_mass ?? 0);
      const raw = massValues[productID] ?? '';
      const parsed = parseFloat(raw);
      const current =
        raw === '' || !Number.isFinite(parsed) || parsed <= 0 ? 0 : parsed;

      if (updatingCartitems && paid > 0 && current < paid) {
        onAddProductMass({
          ...memoizedProduct,
          mass: paid,
        } as CartItem);
        setTextInputError?.((prevErrors: any) => ({
          ...prevErrors,
          [productID]: false,
        }));
        setMassValues(prevValues => ({
          ...prevValues,
          [productID]: String(paid),
        }));
        AppSnackbar.show(
          'This item is fully or partially paid. It cannot be removed. Cancel the order instead.',
          'warning',
          { durationMs: 5000 },
        );
        return;
      }

      const liveMass =
        live?.mass == null ? '' : String(roundToNumericPrecision(live.mass));
      setMassValues(prevValues =>
        prevValues[productID] === liveMass
          ? prevValues
          : { ...prevValues, [productID]: liveMass },
      );
    }, [
      cartItems,
      productID,
      variantId,
      massValues,
      updatingCartitems,
      memoizedProduct,
      onAddProductMass,
      setTextInputError,
    ]);

    // Partial order info - shows PAID/UNPAID status for partial payment orders
    const partialOrderInfo = useMemo(() => {
      if (orderDetails?.orderType?.toLowerCase() !== 'partial') return null;

      if (hasVariants) {
        const attention = getVariantPartialAttention(
          cartItems,
          productID,
          price_type,
        );
        if (!attention) return null;
        const isFullyPaid = attention === 'paid';
        return (
          <Text style={isFullyPaid ? styles.paidText : styles.unpaidText}>
            {isFullyPaid ? 'PAID' : 'UNPAID'}
          </Text>
        );
      }

      // All data from the live cart item — same pattern as master.
      // Cart item carries bp_order_completed + bp_order_paid_* from the order
      // load; no reducer mutates them, so it's the source of truth.
      // No cart row (minus-to-0) → no pill. Backgrounding keeps cart in RAM.
      const liveCartItem = cartItems?.find(
        item =>
          item.productID === productID &&
          (item.variantId ?? null) === (variantId ?? null),
      );
      if (!liveCartItem || liveCartItem.bp_order_completed === undefined)
        return null;

      const paidMass = Number(liveCartItem.bp_order_paid_mass ?? 0);
      const paidQuantity = Number(liveCartItem.bp_order_paid_quantity ?? 0);
      const unpaidMass = Number(liveCartItem.mass ?? 0) - paidMass;
      const unpaidQuantity = Number(liveCartItem.Quantity ?? 0) - paidQuantity;

      const isFullyPaid =
        price_type === 'unit'
          ? unpaidMass <= 0 || isNumericEffectivelyZero(unpaidMass)
          : isNumericEffectivelyZero(unpaidQuantity);

      return (
        <Text style={isFullyPaid ? styles.paidText : styles.unpaidText}>
          {price_type === 'unit'
            ? isFullyPaid
              ? `${formatNumberDecimal(paidMass)} ${unitofmass} PAID`
              : `${formatNumberDecimal(unpaidMass)} ${unitofmass} UNPAID`
            : isFullyPaid
              ? `${formatNumberDecimal(paidQuantity || Number(liveCartItem.Quantity ?? 0))} PAID`
              : `${formatNumberDecimal(unpaidQuantity)} UNPAID`}
        </Text>
      );
    }, [
      orderDetails,
      cartItems,
      productID,
      variantId,
      price_type,
      unitofmass,
      styles,
      hasVariants,
    ]);

    const currencyText = useMemo(
      () => replaceCurrencyString('ksh', currency),
      [currency],
    );

    const priceText = useMemo(() => {
      if (hasVariants) {
        return priceRangeText || formatMoney(price ?? 0);
      }
      if (!price) return '';
      if (price_type === 'unit') {
        const unit = unitofmass ? `/${unitofmass}` : '';
        return `${formatMoney(price)}${unit}`;
      }
      return formatMoney(price);
    }, [hasVariants, priceRangeText, price, price_type, unitofmass]);

    const displayName = useMemo(
      () =>
        getProductDisplayName({
          itemname,
          mass,
          unitofmass,
          size,
          model,
          price_type,
        }),
      [itemname, price_type, mass, unitofmass, size, model],
    );

    const qtyInCart = cartItem?.Quantity ?? 0;
    const massInCart = cartItem?.mass ?? 0;

    const getContainerStyle = useCallback(
      ({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => [
        styles.card,
        useGestureForCard ? styles.cardGesture : null,
        isRowLayout ? styles.cardRow : null,
        // hovered && styles.cardHovered,
        pressed && styles.cardPressed,
      ],
      [styles, useGestureForCard, isRowLayout],
    );

    const getColumnContainerStyle = useCallback(
      ({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => [
        styles.cardVerticalOuter,
        {
          // Floor, not ceiling: variant chips / large font scales grow the card
          // instead of clipping. FlashList grid equalizes heights per row.
          minHeight: showProductImages ? itemSize : itemSize / 1.6,
          flexGrow: 1,
        },
        useGestureForCard ? styles.cardGesture : null,
        // hovered && styles.cardHovered,
        pressed && styles.cardPressed,
      ],
      [styles, showProductImages, itemSize, useGestureForCard],
    );

    const onCardTap = useCallback(() => {
      if (hasVariants) {
        onProductPress?.(memoizedProduct as any);
        return;
      }
      if (!useGestureForCard) return;
      if (stockTracked && quantity <= 0) {
        AppSnackbar.show(
          `${getProductDisplayName({ itemname, mass, unitofmass, price_type })} is out of stock`,
          'warning',
          { durationMs: 4500 },
        );
        return;
      }
      onAdd();
    }, [
      hasVariants,
      onProductPress,
      memoizedProduct,
      useGestureForCard,
      quantity,
      price_type,
      itemname,
      mass,
      unitofmass,
      onAdd,
    ]);

    const onCardLongPress = useCallback(() => {
      if (hasVariants) return;
      if (!useGestureForCard) return;
      onSub();
    }, [hasVariants, useGestureForCard, onSub]);

    // Match QuantityControlButton (+) theme from settings (default: success).
    const controlThemeColors = useMemo(() => {
      type ColorKey = keyof typeof theme.colors;
      const mainKey = ((quantityButtons as any)?.plus?.main ||
        'success') as ColorKey;
      const lightKey = (`${String(mainKey)}Light`) as ColorKey;
      return {
        main: theme.colors[mainKey] ?? theme.colors.success,
        light: theme.colors[lightKey] ?? theme.colors.successLight,
      };
    }, [quantityButtons, theme]);

    // Render quantity controls using shared QuantityControls component
    const renderControls = () => {
      if (hasVariants) {
        // Filled once any variant is in the cart, so the card reads as
        // "added" at a glance; light (as before) when nothing is.
        const inCart = variantCartTotal > 0;
        const contentColor = inCart
          ? theme.colors.textInverse
          : controlThemeColors.main;
        return (
          <Pressable
            onPress={() => onProductPress?.(memoizedProduct as any)}
            style={[
              styles.variantSelectButton,
              {
                backgroundColor: inCart
                  ? controlThemeColors.main
                  : controlThemeColors.light,
              },
            ]}>
            <Text
              style={[styles.variantSelectButtonText, { color: contentColor }]}
              numberOfLines={1}>
              {inCart
                ? price_type === 'unit'
                  ? `${formatNumberDecimal(variantCartTotal)}${unitofmass ? ` ${unitofmass}` : ''}`
                  : `${formatNumberDecimal(variantCartTotal)} item${variantCartTotal !== 1 ? 's' : ''}`
                : `${variantCount} Variant${variantCount !== 1 ? 's' : ''}`}
            </Text>
            <FontAwesome6
              name="chevron-down"
              iconStyle="solid"
              size={iconSize(10)}
              color={contentColor}
            />
          </Pressable>
        );
      }
      // Unit-priced products
      if (price_type === 'unit') {
        return (
          <QuantityControls
            priceType="unit"
            quantity={0}
            mass={massInCart}
            massText={massValues?.[productID]}
            unitOfMass={unitofmass}
            onAdd={onAdd}
            onSubtract={onSub}
            onQuantityChange={() => {}}
            onMassChange={handleMassInputChange}
            onMassBlur={handleMassBlur}
            onInputFocus={onCardInputFocus}
            useWebLayout={true}
            compact={!isRowLayout && useCompactControls}
            hasError={!!(textInputError && textInputError[`${productID}::`])}
            showPlusDisabledStyle={!inStock}
            fixedHeight
          />
        );
      }

      if (useGestureForCard) {
        return (
          <QuantityControls
            priceType="item"
            gestureMode
            quantity={qtyInCart}
            onAdd={() => {}}
            onSubtract={() => {}}
            onQuantityChange={() => {}}
            useWebLayout={true}
            compact={!isRowLayout && useCompactControls}
            fixedHeight
          />
        );
      }

      // Item-priced products
      return (
        <QuantityControls
          priceType="item"
          quantity={qtyInCart}
          onAdd={onAdd}
          onSubtract={onSub}
          onQuantityChange={handleQuantityInputChange}
          onQuantityBlur={handleQuantityBlur}
          onInputFocus={onCardInputFocus}
          useWebLayout={true}
          compact={!isRowLayout && useCompactControls}
          hideSubtractWhenZero={true}
          hasError={!!(quantityInputError && quantityInputError[`${productID}::`])}
          showPlusDisabledStyle={!inStock}
          fixedHeight
        />
      );
    };

    const stockBadge = (() => {
      const qty = Number(quantity ?? 0);
      const threshold = Number(msl ?? 0);
      const hasThreshold = Number.isFinite(threshold) && threshold > 0;
      const isLowStock = hasThreshold && qty > 0 && qty <= threshold;
      const isOutOfStock = qty <= 0;
      if (!stockTracked || (!isLowStock && !isOutOfStock)) return null;

      // Column + images: overlay on image/placeholder. Else card-level.
      const overlayOnImage = !isRowLayout && showProductImages;
      const positionStyle = isRowLayout
        ? [styles.stockIndicator, styles.stockIndicatorRow]
        : overlayOnImage
          ? styles.stockOverlayOnImage
          : styles.stockIndicatorColumn;

      return (
        <View style={[positionStyle, styles.stockIndicatorText]}>
          <Text style={styles.stockCount}>
            {isOutOfStock ? '0' : formatNumberDecimal(qty)}
          </Text>
        </View>
      );
    })();

    const overlayStockOnImage = !isRowLayout && showProductImages;

    const columnImage = showProductImages ? (
      <View
        style={[
          styles.imageContainerVertical,
          { height: verticalImageHeight },
        ]}>
        {image ? (
          <FastImage
            source={{
              uri: forceHttps(image),
              priority: FastImage.priority.high,
              cache: FastImage.cacheControl.immutable,
            }}
            style={styles.productImageVertical}
            resizeMode={FastImage.resizeMode.cover}
          />
        ) : (
          <Pressable
            onPress={() => onMissingImagePress?.(memoizedProduct as any)}
            style={styles.emptyImagePlaceholderVertical}>
            {stockTracked ? (
              <FontAwesome6
                name="image"
                size={iconSize(24)}
                color={theme.colors.textSecondary}
              />
            ) : (
              <MaterialCommunityIcons
                name="briefcase-outline"
                size={iconSize(26)}
                color={theme.colors.textSecondary}
              />
            )}
          </Pressable>
        )}
        {overlayStockOnImage ? stockBadge : null}
      </View>
    ) : null;

    const columnNameBlock = (
      <View style={styles.productInfoVertical}>
        <View style={styles.nameContainerVertical}>
          <Text
            numberOfLines={2}
            ellipsizeMode="tail"
            style={styles.itemNameVertical}>
            {displayName}
          </Text>
        </View>
      </View>
    );

    if (isRowLayout) {
      return (
        <Pressable
          onPress={
            useGestureForCard
              ? onCardTap
              : onProductPress
                ? () => onProductPress(memoizedProduct as any)
                : undefined
          }
          onLongPress={useGestureForCard ? onCardLongPress : undefined}
          delayLongPress={200}
          style={getContainerStyle}>
          {showProductImages ? (
            <View style={styles.imageContainerRow}>
              {image ? (
                <FastImage
                  source={{
                    uri: forceHttps(image),
                    priority: FastImage.priority.high,
                    cache: FastImage.cacheControl.immutable,
                  }}
                  style={styles.imageInnerRow}
                  resizeMode={FastImage.resizeMode.cover}
                />
              ) : (
                <Pressable
                  onPress={() => onMissingImagePress?.(memoizedProduct as any)}
                  style={styles.imageMissingRow}>
                  {stockTracked ? (
                    <FontAwesome6
                      name="image"
                      size={iconSize(24)}
                      color={theme.colors.textSecondary}
                    />
                  ) : (
                    <MaterialCommunityIcons
                      name="briefcase-outline"
                      size={iconSize(26)}
                      color={theme.colors.textSecondary}
                    />
                  )}
                </Pressable>
              )}
            </View>
          ) : (
            <View style={styles.imageContainerRow} />
          )}
          <View style={styles.metaRow}>
            <Text numberOfLines={2} style={styles.nameRow}>
              {displayName}
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.categoryRow}>
              {appendServiceMeta(category, kind) || '—'}
            </Text>
          </View>
          <View style={styles.priceControlsRow}>
            <View style={styles.priceUnpaidRowCompact}>
              <Text numberOfLines={1} style={styles.priceRow}>
                {currencyText} {priceText}
              </Text>
              {partialOrderInfo ? (
                <View style={styles.partialOrderInfoContainer}>
                  {partialOrderInfo}
                </View>
              ) : null}
            </View>
            <Pressable
              onPress={e => e.stopPropagation()}
              style={styles.controlsRow}>
              {renderControls()}
            </Pressable>
          </View>
          {stockBadge}
        </Pressable>
      );
    }

    // Column layout — mirrors Product.tsx vertical card composition
    return (
      <Pressable
        onPress={
          useGestureForCard
            ? onCardTap
            : onProductPress
              ? () => onProductPress(memoizedProduct as any)
              : undefined
        }
        onLongPress={useGestureForCard ? onCardLongPress : undefined}
        delayLongPress={200}
        onLayout={onColumnCardLayout}
        style={getColumnContainerStyle}>
        <View style={styles.cardVertical}>
          {!overlayStockOnImage ? stockBadge : null}

          {useGestureForCard ? (
            <View style={styles.columnTopBlock}>
              {columnImage}
              {columnNameBlock}
            </View>
          ) : (
            <Pressable
              onPress={() => onProductPress?.(memoizedProduct as any)}
              style={styles.columnTopBlock}>
              {columnImage}
              {columnNameBlock}
            </Pressable>
          )}

          <View style={styles.priceControlsVertical}>
            {partialOrderInfo ? (
              <View style={styles.priceUnpaidRow}>
                <Text numberOfLines={1} style={styles.priceTextInRow}>
                  {currencyText} {priceText}
                </Text>
                <View style={styles.partialOrderInfoContainer}>
                  {partialOrderInfo}
                </View>
              </View>
            ) : (
              <Text numberOfLines={1} style={styles.priceTextVertical}>
                {currencyText} {priceText}
              </Text>
            )}
            <Pressable
              onPress={e => e.stopPropagation()}
              style={styles.controlsWrapperVertical}>
              {renderControls()}
            </Pressable>
          </View>
        </View>
      </Pressable>
    );
  },
);

export default ProductItem;

const createStyles = (theme: any) =>
  StyleSheet.create({
    // Row card chrome (unchanged)
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.sm,
      overflow: 'hidden',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      boxShadow: `0px ${v(4)}px ${s(12)}px rgba(0, 0, 0, 0.08)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease-out',
    } as any,
    // Column card chrome — matches Product.tsx containerStyle (vertical)
    cardVerticalOuter: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.sm,
      borderWidth: borderWidth(0.25),
      borderColor: theme.colors.border,
      boxShadow: `0px ${v(0.5)}px ${s(1)}px rgba(0, 0, 0, 0.2)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease-out',
      width: '100%',
      paddingVertical: s(5),
      paddingHorizontal: s(3),
      marginBottom: v(5),
      marginTop: v(5),
      overflow: 'hidden',
    } as any,
    cardVertical: {
      flexDirection: 'column',
      position: 'relative',
      flex: 1,
    },
    columnTopBlock: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    // cardHovered: {
    //   transform: [{ translateY: -2 }],
    // },

    cardPressed: {
      opacity: 0.1,
    },
    cardGesture: {},
    cardRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: s(8),
      minHeight: v(80),
      position: 'relative',
    },

    // Row layout - left image
    imageContainerRow: {
      width: s(64),
      height: s(64),
      borderRadius: theme.borderRadius.sm,
      overflow: 'hidden',
      backgroundColor: theme.colors.surfaceSecondary,
    },
    imageInnerRow: {
      width: '100%',
      height: '100%',
    } as any,
    imageMissingRow: {
      width: '100%',
      height: '100%',
      borderRadius: theme.borderRadius.sm,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.backgroundGray100,
    },

    // Row layout - center meta
    metaRow: {
      flex: 1,
      paddingHorizontal: s(10),
      justifyContent: 'center',
    },
    variantBadge: {
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.primaryLight,
      borderRadius: theme.borderRadius.sm,
      paddingHorizontal: s(8),
      paddingVertical: v(3),
      marginBottom: v(4),
    },
    variantBadgeText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      lineHeight: fs(13),
      color: theme.colors.primary,
    },
    nameRow: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(15),
      color: theme.colors.text,
      marginBottom: v(5),
    },
    categoryRow: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(13),
      color: theme.colors.textSecondary,
      marginTop: v(5),
      marginBottom: v(5),
    },

    // Row layout - right price + controls
    priceControlsRow: {
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    priceRow: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(20),
      color: theme.colors.text,
      marginBottom: v(4),
      marginRight: s(10),
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    controlsRow: {
      width: s(180),
    },

    // Column layout — mirrors Product.tsx vertical image/name/price
    imageContainerVertical: {
      width: '100%',
      marginBottom: v(6),
      borderRadius: theme.borderRadius.sm,
      overflow: 'hidden',
      backgroundColor: theme.colors.backgroundGray100,
    },
    productImageVertical: {
      width: '100%',
      height: '100%',
      transform: [{ scale: 1.08 }],
    } as any,
    emptyImagePlaceholderVertical: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.backgroundGray100,
    },
    productInfoVertical: {
      width: '100%',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
    nameContainerVertical: {
      width: '100%',
      height: v(34),
    },
    itemNameVertical: {
      width: '100%',
      marginTop: v(3),
      textAlign: 'left',
      lineHeight: fs(17),
      height: v(34),
      fontSize: fs(15),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      overflow: 'hidden',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    priceControlsVertical: {
      marginTop: v(4),
    },
    priceTextVertical: {
      width: '100%',
      textAlign: 'left',
      fontSize: fs(13),
      lineHeight: fs(20),
      marginTop: v(2),
      marginBottom: v(4),
      fontFamily: GTFonts.bold,
      color: theme.colors.textGray700,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    priceTextInRow: {
      fontSize: fs(13),
      lineHeight: fs(20),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
    },
    controlsWrapperVertical: {
      flexDirection: 'row',
      justifyContent: 'center',
      width: '100%',
      marginTop: v(3),
      alignItems: 'center',
    },

    // Stock indicator
    stockIndicator: {
      position: 'absolute',
      top: v(6),
      right: s(6),
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3,
    },
    stockOverlayOnImage: {
      position: 'absolute',
      top: v(4),
      right: s(4),
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3,
    },
    stockIndicatorColumn: {
      position: 'absolute',
      top: v(4),
      right: s(4),
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3,
    },
    stockIndicatorRow: {
      top: v(2),
      right: s(4),
    },
    stockIndicatorText: {
      minWidth: s(22),
      height: s(22),
      paddingHorizontal: s(4),
    },
    stockCount: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      color: theme.colors.error,
      alignSelf: 'center',
    },

    priceUnpaidRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      gap: s(6),
    },
    priceUnpaidRowCompact: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      marginBottom: v(8),
    },

    // Partial order info styles
    partialOrderInfoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
    },
    paidText: {
      fontSize: fs(11),
      fontFamily: GTFonts.semibold,
      color: theme.colors.success,
    },
    unpaidText: {
      fontSize: fs(11),
      fontFamily: GTFonts.semibold,
      color: theme.colors.error,
    },

    // Match QuantityControls unit input width; no border like QuantityControlButton (+).
    // Dropdown layout: value on the left, chevron pinned to the right edge.
    variantSelectButton: {
      width: s(130),
      minHeight: v(37),
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(8),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(4),
    },
    variantSelectButtonText: {
      flexShrink: 1,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(14),
    },
  });
